import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { AdvertisingMaterial } from '../LogistikProduct.js';
import { LogistikProductFilter } from '../LogistikProductFilter.js';

const subscribeAdvertisingMaterials = registerSubscription({
  type: () => [AdvertisingMaterial],
  operationName: 'subscribeAdvertisingMaterials',
  options: {},
  params: [],
  resolve: () => {
    return (gqlFields: BaseQLSelectionSet<AdvertisingMaterial>) => {
      return from(publishFilter(AdvertisingMaterial._class)).pipe(
        switchMap(publishFilter => {
          return watchQuery<AdvertisingMaterial>(
            AdvertisingMaterial._collectionName,
            publishFilter
              ? {
                  $and: [
                    publishFilter,
                    { isPublished: { $ne: false } },
                    { _class: { $in: baseqlMetaData.entities.validSubclasses(AdvertisingMaterial) } },
                  ],
                }
              : {
                  isPublished: { $ne: false },
                  _class: { $in: baseqlMetaData.entities.validSubclasses(AdvertisingMaterial) },
                },
            {
              sort: { _id: -1 },
            },
          );
        }),
        catchError(err => {
          logger.error({ err }, 'Fehler in subscribeAdvertisingMaterials');
          return of([] as AdvertisingMaterial[]);
        }),
      );
    };
  },
});

const subscribeAdvertisingMaterialsPaginated = registerSubscription({
  type: () => [AdvertisingMaterial],
  operationName: 'subscribeAdvertisingMaterialsPaginated',
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LogistikProductFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: LogistikProductFilter) => {
    return (gqlFields: BaseQLSelectionSet<AdvertisingMaterial>) => {
      const selector = filter?.getMongoFilter() ?? {};
      return from(publishFilter(AdvertisingMaterial._class)).pipe(
        switchMap(publishFilter => {
          return watchQuery(
            AdvertisingMaterial._collectionName,
            publishFilter
              ? {
                  $and: [
                    publishFilter,
                    selector,
                    { isPublished: { $ne: false } },
                    { _class: { $in: baseqlMetaData.entities.validSubclasses(AdvertisingMaterial) } },
                  ],
                }
              : {
                  selector,
                  isPublished: { $ne: false },
                  _class: { $in: baseqlMetaData.entities.validSubclasses(AdvertisingMaterial) },
                },
            {
              sort: filter.getMongoOptions().sort,
            },
          );
        }),
        catchError(err => {
          logger.error({ err }, 'Fehler in subscribeAdvertisingMaterialsPaginated');
          return of([] as AdvertisingMaterial[]);
        }),
      );
    };
  },
});

const subscribePublishedAdvertisingMaterialsCount = registerSubscription({
  type: () => Int,
  operationName: 'subscribePublishedAdvertisingMaterialsCount',
  options: {},
  params: [],
  resolve: () => {
    return () => {
      return watchQuery(
        AdvertisingMaterial._collectionName,
        { _class: { $in: baseqlMetaData.entities.validSubclasses(AdvertisingMaterial) }, isPublished: { $ne: false } },
        { projection: { _id: 1 } },
      ).pipe(map(learningMaterials => learningMaterials.length));
    };
  },
});

export namespace AdvertisingMaterialAPI {
  export const subscribe = subscribeAdvertisingMaterials;
  export const subscribePaginated = subscribeAdvertisingMaterialsPaginated;
  export const count = subscribePublishedAdvertisingMaterialsCount;
}
